import { RouteObject } from "react-router-dom";
import { Role, isAuthorized } from "../../auth/authConfig";
import { lazy } from "react";
import Root from "../../root";
const RedirectToOverview = lazy(() => import("./RedirectToOverview"));
const FirewallRequest = lazy(() => import("../../routes/firewallRequest/firewallRequest"));
const RunRequestManagement = lazy(() => import("../../routes/runRequestManagement/runRequestManagement"));
const MappingMeasuringPointsTransformerModels = lazy(
  () => import("../../routes/administration/mappingMeasuringPointsTransformerModels/mappingMeasuringPointsTransformerModels")
);
const Overview = lazy(() => import("../../routes/overview/overview"));
const PQEvents = lazy(() => import("../../routes/pqEvents/pqEvents"));
const Groups = lazy(() => import("../../routes/pqEvents/groups/groups"));
const GroupAdministration = lazy(() => import("../../routes/pqEvents/groupAdministration/groupAdministration"));
const ActiveMeasuringPoints = lazy(() => import("../../routes/activeMeasuringPoints/activeMeasuringPoints"));
const Devices = lazy(() => import("../../routes/administration/devices/devices"));
const MeasuringPoints = lazy(() => import("../../routes/administration/measuringPoints/measuringPoints"));
const OperationalEvents = lazy(() => import("../../routes/operationalEvents/operationalEvents"));
const DashboardAnalysis = lazy(() => import("../../routes/dashboardAnalysis/dashboardAnalysis"));
const GridEvents = lazy(() => import("../../routes/gridEvents/gridEvents"));
const IncidentManagement = lazy(() => import("../../routes/incidentManagement/incidentManagement"));
const MasterData = lazy(() => import("../../routes/administration/masterData/masterData"));
const DataCompleteness = lazy(() => import("../../routes/dataCompleteness/dataCompleteness"));
const DataPlausibility = lazy(() => import("../../routes/dataPlausibility/dataPlausibility"));
const Correlation = lazy(() => import("../../routes/analyse/correlation/correlation"));
const Anomaly = lazy(() => import("../../routes/analyse/anomaly/anomaly"));
const Trends = lazy(() => import("../../routes/analyse/trends/trends"));
const LambdaControl = lazy(() => import("../../routes/lambdaControl/lambdaControl"));
const Router = lazy(() => import("../../routes/administration/router/router"));
const PQIndex = lazy(() => import("../../routes/analyse/pqIndex/pqIndex"));

export type CustomRouteObject = Omit<RouteObject, "children"> & {
  title: string;
  params?: string;
  showInMenu: Record<"dev" | "test" | "prod", Role[]>;
  children?: CustomRouteObject[];
};

export function getRoutes(navItems: CustomRouteObject[]): RouteObject[] {
  const root: RouteObject = {
    path: "/",
    Component: Root,
  };

  const defaultRoute: RouteObject = {
    path: "*",
    Component: Overview,
  };

  const children = navItems
    .flatMap(({ path, params, Component, children, index }) => {
      const route: RouteObject = {
        path: params ? path + params : path,
        Component,
        index,
      };
      if (children) {
        return children
          .map(({ path, params, Component, index }) => {
            const route: RouteObject = {
              path: params ? path + params : path,
              Component,
              index,
            };
            return route;
          })
          .concat(route);
      }
      return route;
    })
    .concat(defaultRoute);

  root.children = children;
  return [root];
}

export function getAuthorizedNavItems(): CustomRouteObject[] {
  let environment = import.meta.env.VITE_ENVIRONMENT;
  if (environment !== "dev" && environment !== "test" && environment !== "prod") environment = "prod";
  return navItems
    .filter(({ showInMenu }) => isAuthorized(showInMenu[environment]))
    .reduce(
      (items: CustomRouteObject[], item) =>
        items.concat({ ...item, children: item.children?.filter(({ showInMenu }) => isAuthorized(showInMenu[environment])) }),
      []
    );
}

export const navItems: CustomRouteObject[] = [
  { title: "HiddenRedirectRoute", Component: RedirectToOverview, index: true, showInMenu: { prod: [], test: [], dev: [] } },
  {
    title: "Übersicht",
    Component: Overview,
    path: "/uebersicht",
    showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
    children: [
      {
        title: "PQ-Ereignisse",
        Component: PQEvents,
        path: "/uebersicht/pqereignisse",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Gruppen",
        Component: Groups,
        path: "/uebersicht/gruppen",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Gruppenverwaltung",
        Component: GroupAdministration,
        path: "/uebersicht/gruppenverwaltung",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
    ],
  },
  {
    title: "Geräteverwaltung",
    Component: ActiveMeasuringPoints,
    path: "/geraeteverwaltung",
    params: "/:id?",
    showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
    children: [
      {
        title: "Aktive Messpunkte",
        Component: ActiveMeasuringPoints,
        path: "/geraeteverwaltung/aktivemesspunkte",
        params: "/:id?",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Messstellen",
        Component: MeasuringPoints,
        path: "/geraeteverwaltung/messstellen",
        params: "/:id?",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Messgeräte",
        Component: Devices,
        path: "/geraeteverwaltung/messgeraete",
        params: "/:id?",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Router",
        Component: Router,
        path: "/geraeteverwaltung/router",
        params: "/:id?",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Betriebsereignisse",
        Component: OperationalEvents,
        path: "/geraeteverwaltung/betriebsereignisse",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "IP-Verwaltung",
        Component: FirewallRequest,
        path: "/geraeteverwaltung/ipverwaltung",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
    ],
  },
  {
    title: "Dashboard Analyse",
    Component: DashboardAnalysis,
    path: "/dashboardAnalyse",
    showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
  },
  {
    title: "Netzereignisse",
    Component: GridEvents,
    path: "/netzereignisse",
    params: "/:id?",
    showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
  },
  {
    title: "Störungsmanagement",
    Component: IncidentManagement,
    path: "/stoerungsmanagement",
    showInMenu: { prod: [], test: [], dev: [] },
  },
  {
    title: "Administration",
    Component: DataCompleteness,
    path: "/administration",
    showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
    children: [
      {
        title: "Stammdaten",
        Component: MasterData,
        path: "/administration/stammdaten",
        params: "/:list?/:id?",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Datenvollständigkeit",
        Component: DataCompleteness,
        path: "/administration/datenvollstaendigkeit",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Datenplausibilität",
        Component: DataPlausibility,
        path: "/administration/datenplausibilitaet",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Verbaute Wandler",
        Component: MappingMeasuringPointsTransformerModels,
        path: "/administration/verbautewandler",
        showInMenu: { prod: ["Everyone"], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Job Management",
        Component: RunRequestManagement,
        path: "/administration/jobmanagement",
        showInMenu: { prod: ["Admin"], test: ["Admin"], dev: ["Admin"] },
      },
      {
        title: "Lambda control",
        Component: LambdaControl,
        path: "/administration/lambdacontrol",
        showInMenu: { prod: [], test: [], dev: ["Backend"] },
      },
    ],
  },
  {
    title: "Analyse",
    Component: PQIndex,
    path: "/analyse",
    showInMenu: { prod: [], test: ["Everyone"], dev: ["Everyone"] },
    children: [
      {
        title: "PQ Index",
        Component: PQIndex,
        path: "/analyse/pqindex",
        showInMenu: { prod: [], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Korrelation",
        Component: Correlation,
        path: "/analyse/korrelation",
        params: "/:id?",
        showInMenu: { prod: [], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Anomalie",
        Component: Anomaly,
        path: "/analyse/anomalie",
        showInMenu: { prod: [], test: ["Everyone"], dev: ["Everyone"] },
      },
      {
        title: "Trends",
        Component: Trends,
        path: "/analyse/trends",
        params: "/:id?",
        showInMenu: { prod: [], test: ["Everyone"], dev: ["Everyone"] },
      },
    ],
  },
];
